import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Include scripts (like analytics).
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains, metaPixelId } = analytics;

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  let analyticsLibraries = [];

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin="anonymous"
      ></script>
    );

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      // Ensure that gtag function is found from window scope
      window.gtag = function gtag() {
        dataLayer.push(arguments);
      };
      gtag('js', new Date());
      gtag('config', googleAnalyticsId, {
        cookie_flags: 'SameSite=None;Secure',
      });
    }
  }

  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin="anonymous"
      ></script>
    );
  }

  if (metaPixelId) {
    // Add Meta/Facebook Pixel script - following Google Analytics pattern
    // Load the external fbevents.js script
    analyticsLibraries.push(
      <script
        key="fb-events"
        async
        src="https://connect.facebook.net/en_US/fbevents.js"
        crossOrigin
      ></script>
    );

    // Initialize Facebook Pixel using window manipulation (like Google Analytics)
    if (typeof window !== 'undefined') {
      // Initialize fbq function if it doesn't exist
      if (!window.fbq) {
        window.fbq = function() {
          window.fbq.callMethod
            ? window.fbq.callMethod.apply(window.fbq, arguments)
            : window.fbq.queue.push(arguments);
        };
        window.fbq.push = window.fbq;
        window.fbq.loaded = true;
        window.fbq.version = '2.0';
        window.fbq.queue = [];
      }

      // Initialize the pixel with the ID
      window.fbq('init', metaPixelId);
      window.fbq('track', 'PageView');

      // Add noscript fallback image
      const img = document.createElement('img');
      img.height = '1';
      img.width = '1';
      img.style.display = 'none';
      img.src = `https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1`;
      document.body.appendChild(img);
    }
  }

  return <Helmet>{analyticsLibraries}</Helmet>;
};
